import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getAllFabricOptions, approveFabricOption, rejectFabricOption } from '../../store/actions';
import { Grid, List, GitCompare, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import Header from '../../components/layout/Header';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { FABRIC_DECISION, formatDate, formatINR, label, options } from '../../constants/garmentCodes';

const DECISION = { SUBMITTED: 2, UNDER_REVIEW: 3, APPROVED: 4, REJECTED: 5, MORE_OPTIONS: 6 };
const DECISION_VARIANT = { 4: 'green', 5: 'red', 6: 'purple', 2: 'amber', 3: 'amber' };
const REJECT_REASONS = ['Price too high', 'Incorrect colour', 'GSM mismatch', 'MOQ too high', 'Lead time too long', 'Quality concern', 'Not suitable'];
const MAX_COMPARE = 3;
const COMPARE_ROWS = [
  ['Material', 'material'],
  ['Composition', 'composition'],
  ['GSM', 'gsm'],
  ['Width', 'width'],
  ['Finish', 'finish'],
  ['Stretch', 'stretch'],
  ['Price', 'pricePerUnit', formatINR],
  ['MOQ', 'moq'],
  ['Lead time', 'leadTime'],
];

const isPending = (f) => f.decisionStatus === DECISION.SUBMITTED || f.decisionStatus === DECISION.UNDER_REVIEW;
const decisionBadge = (code) => <Badge variant={DECISION_VARIANT[code] || 'gray'}>{label(FABRIC_DECISION, code, 'Pending')}</Badge>;

function FabricImage({ fabric, className }) {
  if (fabric.image) return <img src={fabric.image} alt={fabric.name} className={`${className} object-cover`} />;
  return <div className={className} style={{ backgroundColor: fabric.colourCode || '#F4F3F0' }} />;
}

export default function BrandFabrics() {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const requirementId = params.get('requirementId') || '';
  const [view, setView] = useState('gallery');
  const [decisionFilter, setDecisionFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [compare, setCompare] = useState([]);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approvalNote, setApprovalNote] = useState('');
  const [approveConfirmed, setApproveConfirmed] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectMode, setRejectMode] = useState(DECISION.REJECTED);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectComment, setRejectComment] = useState('');

  const fabrics = useSelector((state) => state.fabricoption.options);
  const loading = useSelector((state) => state.fabricoption.loading);
  const brandId = useSelector((state) => state.brand.profile?.id);

  useEffect(() => {
    dispatch(getAllFabricOptions({ brandId, requirementId, decisionStatus: decisionFilter }));
  }, [dispatch, brandId, requirementId, decisionFilter]);

  const groups = useMemo(
    () =>
      fabrics.reduce((acc, f) => {
        const key = f.requirementName || 'Unassigned';
        (acc[key] = acc[key] || []).push(f);
        return acc;
      }, {}),
    [fabrics],
  );

  const inCompare = (id) => compare.some((c) => String(c.id) === String(id));
  const toggleCompare = (fab) =>
    setCompare((prev) => (inCompare(fab.id) ? prev.filter((c) => String(c.id) !== String(fab.id)) : [...prev, fab].slice(-MAX_COMPARE)));

  const openApprove = (fab) => {
    setSelected(null);
    setApprovalNote('');
    setApproveConfirmed(false);
    setApproveTarget(fab);
  };
  const openReject = (fab, mode) => {
    setSelected(null);
    setRejectReason(mode === DECISION.MORE_OPTIONS ? 'More options requested' : '');
    setRejectComment('');
    setRejectMode(mode);
    setRejectTarget(fab);
  };

  const confirmApprove = () => {
    dispatch(approveFabricOption({ id: approveTarget.id, decisionStatus: DECISION.APPROVED, approvalNote: approvalNote.trim() || undefined }));
    setApproveTarget(null);
  };
  const confirmReject = () => {
    dispatch(rejectFabricOption({ id: rejectTarget.id, decisionStatus: rejectMode, rejectionReason: [rejectReason, rejectComment.trim()].filter(Boolean).join(' — ') }));
    setRejectTarget(null);
  };

  const actionButtons = (fab, size = 'xs') =>
    isPending(fab) && (
      <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
        <Button variant="success" size={size} icon={<CheckCircle size={11} />} className="flex-1" onClick={() => openApprove(fab)}>Approve</Button>
        <Button variant="danger" size={size} icon={<XCircle size={11} />} className="flex-1" onClick={() => openReject(fab, DECISION.REJECTED)}>Reject</Button>
      </div>
    );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header
        title="Fabrics"
        subtitle={`${fabrics.length} options`}
        actions={
          <div className="flex rounded-lg border border-[#E5E3DE] overflow-hidden">
            {[{ key: 'gallery', icon: Grid }, { key: 'compare', icon: GitCompare }, { key: 'table', icon: List }].map(({ key, icon: Icon }) => (
              <button key={key} onClick={() => setView(key)} className={`p-2 transition-colors ${view === key ? 'bg-[#1E2A3B] text-white' : 'bg-white text-[#9B9893] hover:bg-[#F4F3F0]'}`}>
                <Icon size={14} />
              </button>
            ))}
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="flex items-center gap-3 mb-4">
          <select value={decisionFilter} onChange={(e) => setDecisionFilter(e.target.value)} className="text-sm border border-[#E5E3DE] rounded-lg px-3 py-1.5 bg-white outline-none">
            <option value="">All decisions</option>
            {options(FABRIC_DECISION).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {loading && <span className="text-xs text-[#9B9893]">Loading…</span>}
          {compare.length >= 2 && view !== 'compare' && (
            <Button variant="outline" size="sm" icon={<GitCompare size={12} />} onClick={() => setView('compare')}>Compare {compare.length}</Button>
          )}
        </div>

        {!loading && fabrics.length === 0 && <div className="bg-white rounded-xl border border-[#E5E3DE] py-12 text-center text-sm text-[#9B9893]">No fabric options shared yet</div>}

        {view === 'gallery' && (
          <div className="space-y-6">
            {Object.entries(groups).map(([requirementName, items]) => (
              <div key={requirementName}>
                <div className="mb-3">
                  <h2 className="text-sm font-semibold text-[#1A1915]" style={{ fontFamily: 'Manrope, sans-serif' }}>{requirementName}</h2>
                  <p className="text-xs text-[#9B9893]">{items[0]?.manufacturerName} · {items.length} options</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {items.map((fab) => (
                    <div
                      key={fab.id}
                      onClick={() => setSelected(fab)}
                      className={`bg-white rounded-xl border overflow-hidden hover:shadow-md transition-all cursor-pointer group ${inCompare(fab.id) ? 'border-[#3D52A0] ring-2 ring-[#3D52A0]/20' : 'border-[#E5E3DE]'}`}
                    >
                      <div className="relative h-44 bg-[#F4F3F0]">
                        <FabricImage fabric={fab} className="w-full h-full" />
                        <div className="absolute top-2 left-2">{decisionBadge(fab.decisionStatus)}</div>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleCompare(fab); }}
                          className={`absolute top-2 right-2 p-1.5 rounded-lg text-xs transition-all ${inCompare(fab.id) ? 'bg-[#3D52A0] text-white' : 'bg-white/80 text-[#5C5A54] opacity-0 group-hover:opacity-100'}`}
                        >
                          <GitCompare size={12} />
                        </button>
                      </div>
                      <div className="p-4">
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full border border-[#E5E3DE] shrink-0 mt-0.5" style={{ backgroundColor: fab.colourCode }} />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[#1A1915] truncate">{fab.name}</p>
                            <p className="text-xs font-mono text-[#9B9893]">{fab.code || '—'}</p>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-1.5 text-xs">
                          <div><span className="text-[#9B9893]">GSM</span><span className="ml-1 font-medium text-[#1A1915]">{fab.gsm ?? '—'}</span></div>
                          <div><span className="text-[#9B9893]">Price</span><span className="ml-1 font-medium text-[#1A1915]">{formatINR(fab.pricePerUnit)}</span></div>
                          <div><span className="text-[#9B9893]">MOQ</span><span className="ml-1 font-medium text-[#1A1915]">{fab.moq ?? '—'}</span></div>
                          <div><span className="text-[#9B9893]">Lead</span><span className="ml-1 font-medium text-[#1A1915]">{fab.leadTime || '—'}</span></div>
                        </div>
                        <div className="mt-3">{actionButtons(fab)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {view === 'compare' && (
          <div>
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-[#1A1915]">Comparison View</h2>
              <p className="text-xs text-[#9B9893] mt-0.5">Select up to {MAX_COMPARE} options from the gallery. Rows that differ are highlighted.</p>
            </div>
            {compare.length < 2 ? (
              <div className="text-center py-16">
                <GitCompare size={32} className="text-[#C9C7C2] mx-auto mb-3" />
                <p className="text-sm font-medium text-[#5C5A54]">Select options to compare</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => setView('gallery')}>Back to gallery</Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E5E3DE]">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#9B9893] uppercase tracking-wider w-40">Attribute</th>
                      {compare.map((f) => (
                        <th key={f.id} className="px-4 py-3 text-center min-w-48">
                          <FabricImage fabric={f} className="w-full h-28 rounded-lg mb-2" />
                          <p className="text-sm font-semibold text-[#1A1915]">{f.name}</p>
                          <p className="text-[10px] font-mono text-[#9B9893]">{f.code}</p>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {COMPARE_ROWS.map(([text, key, format]) => {
                      const same = compare.every((f) => f[key] === compare[0][key]);
                      return (
                        <tr key={key} className={same ? '' : 'bg-[#FFFBF0]'}>
                          <td className="px-4 py-3 text-xs font-semibold text-[#5C5A54]">{text}</td>
                          {compare.map((f) => <td key={f.id} className="px-4 py-3 text-center text-sm">{f[key] == null ? '—' : format ? format(f[key]) : f[key]}</td>)}
                        </tr>
                      );
                    })}
                    <tr>
                      <td className="px-4 py-3 text-xs font-semibold text-[#5C5A54]">Decision</td>
                      {compare.map((f) => <td key={f.id} className="px-4 py-3 text-center">{decisionBadge(f.decisionStatus)}</td>)}
                    </tr>
                    <tr>
                      <td className="px-4 py-3" />
                      {compare.map((f) => <td key={f.id} className="px-4 py-3">{actionButtons(f)}</td>)}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {view === 'table' && fabrics.length > 0 && (
          <div className="bg-white rounded-xl border border-[#E5E3DE] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#F4F3F0] bg-[#FAFAF8]">
                    {['Code', 'Name', 'Requirement', 'Material', 'GSM', 'Colour', 'Price', 'MOQ', 'Lead time', 'Decision', 'Actions'].map((h) => (
                      <th key={h} className="text-left px-4 py-3 text-[10px] font-semibold text-[#9B9893] uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F4F3F0]">
                  {fabrics.map((f) => (
                    <tr key={f.id} className="hover:bg-[#FAFAF8] transition-colors cursor-pointer" onClick={() => setSelected(f)}>
                      <td className="px-4 py-3 font-mono text-xs text-[#3D52A0]">{f.code || '—'}</td>
                      <td className="px-4 py-3 font-medium text-[#1A1915]">{f.name}</td>
                      <td className="px-4 py-3 text-xs text-[#5C5A54]">{f.requirementName || '—'}</td>
                      <td className="px-4 py-3 text-xs text-[#5C5A54]">{f.material || '—'}</td>
                      <td className="px-4 py-3 text-xs text-[#5C5A54]">{f.gsm ?? '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full border border-[#E5E3DE]" style={{ backgroundColor: f.colourCode }} />
                          <span className="text-xs text-[#5C5A54]">{f.colour || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs font-medium text-[#1A1915]">{formatINR(f.pricePerUnit)}</td>
                      <td className="px-4 py-3 text-xs text-[#5C5A54]">{f.moq ?? '—'}</td>
                      <td className="px-4 py-3 text-xs text-[#5C5A54]">{f.leadTime || '—'}</td>
                      <td className="px-4 py-3">{decisionBadge(f.decisionStatus)}</td>
                      <td className="px-4 py-3">{actionButtons(f)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
          <div className="relative ml-auto w-full max-w-2xl bg-white shadow-2xl flex flex-col overflow-y-auto">
            <div className="relative h-64 bg-[#F4F3F0]">
              <FabricImage fabric={selected} className="w-full h-full" />
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm p-2 rounded-lg hover:bg-white transition-colors">
                <XCircle size={16} className="text-[#5C5A54]" />
              </button>
              <div className="absolute bottom-4 left-4">{decisionBadge(selected.decisionStatus)}</div>
            </div>
            <div className="p-6 space-y-5 flex-1">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full border-2 border-[#E5E3DE] shrink-0" style={{ backgroundColor: selected.colourCode }} />
                <div>
                  <h2 className="text-lg font-bold text-[#1A1915]" style={{ fontFamily: 'Manrope, sans-serif' }}>{selected.name}</h2>
                  <p className="text-xs font-mono text-[#9B9893]">{selected.code}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Material', selected.material],
                  ['Composition', selected.composition],
                  ['GSM', selected.gsm != null ? `${selected.gsm} gsm` : null],
                  ['Width', selected.width],
                  ['Finish', selected.finish],
                  ['Texture', selected.texture],
                  ['Stretch', selected.stretch],
                  ['Supplier', selected.supplier],
                  ['Submitted', selected.submittedDate ? formatDate(selected.submittedDate) : null],
                  ['Manufacturer', selected.manufacturerName],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[10px] text-[#9B9893] uppercase tracking-wider font-medium">{k}</p>
                    <p className="text-sm text-[#1A1915] mt-0.5">{v ?? '—'}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#E5E3DE] pt-4 grid grid-cols-3 gap-3">
                <div className="bg-[#F4F3F0] rounded-lg p-3 text-center"><p className="text-[10px] text-[#9B9893] uppercase">Price</p><p className="text-base font-bold text-[#1A1915]">{formatINR(selected.pricePerUnit)}</p></div>
                <div className="bg-[#F4F3F0] rounded-lg p-3 text-center"><p className="text-[10px] text-[#9B9893] uppercase">MOQ</p><p className="text-base font-bold text-[#1A1915]">{selected.moq ?? '—'}</p></div>
                <div className="bg-[#F4F3F0] rounded-lg p-3 text-center"><p className="text-[10px] text-[#9B9893] uppercase">Lead time</p><p className="text-base font-bold text-[#1A1915]">{selected.leadTime || '—'}</p></div>
              </div>
              {selected.decisionStatus === DECISION.APPROVED && (
                <div className="bg-[#E8F5EE] border border-[#A8DEC0] rounded-xl p-4">
                  <p className="text-sm font-semibold text-[#1A6B3E]">✓ Approved {selected.approvedDate ? `on ${formatDate(selected.approvedDate)}` : ''}</p>
                  {selected.approvalNote && <p className="text-xs text-[#1A6B3E]/80 mt-0.5">{selected.approvalNote}</p>}
                </div>
              )}
              {(selected.decisionStatus === DECISION.REJECTED || selected.decisionStatus === DECISION.MORE_OPTIONS) && (
                <div className="bg-[#FDECEA] border border-[#F5A9AC] rounded-xl p-4">
                  <p className="text-sm font-semibold text-[#C0222B]">{label(FABRIC_DECISION, selected.decisionStatus)} — {selected.rejectionReason}</p>
                </div>
              )}
              {isPending(selected) && (
                <div className="flex gap-3">
                  <Button variant="success" size="md" icon={<CheckCircle size={14} />} className="flex-1" onClick={() => openApprove(selected)}>Approve</Button>
                  <Button variant="danger" size="md" icon={<XCircle size={14} />} className="flex-1" onClick={() => openReject(selected, DECISION.REJECTED)}>Reject</Button>
                  <Button variant="secondary" size="md" icon={<RefreshCw size={14} />} onClick={() => openReject(selected, DECISION.MORE_OPTIONS)}>More options</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal
        open={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        title="Approve Fabric Option"
        subtitle={approveTarget?.name}
        size="sm"
        footer={
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setApproveTarget(null)}>Cancel</Button>
            <Button variant="success" icon={<CheckCircle size={14} />} disabled={!approveConfirmed} onClick={confirmApprove}>Confirm approval</Button>
          </div>
        }
      >
        {approveTarget && (
          <div className="space-y-4">
            <div className="bg-[#E8F5EE] border border-[#A8DEC0] rounded-xl p-4 text-xs text-[#1A6B3E]">Approving records a decision against the current fabric images and specifications.</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#9B9893]">Price</span><span className="font-medium">{formatINR(approveTarget.pricePerUnit)} {approveTarget.uom || ''}</span></div>
              <div className="flex justify-between"><span className="text-[#9B9893]">MOQ</span><span className="font-medium">{approveTarget.moq ?? '—'}</span></div>
              <div className="flex justify-between"><span className="text-[#9B9893]">Lead time</span><span className="font-medium">{approveTarget.leadTime || '—'}</span></div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C5A54]">Approval note (optional)</label>
              <textarea rows={2} value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} className="w-full mt-1.5 px-3 py-2 text-sm border border-[#E5E3DE] rounded-lg outline-none focus:border-[#3D52A0] resize-none" />
            </div>
            <label className="flex items-start gap-2 text-xs text-[#5C5A54]">
              <input type="checkbox" checked={approveConfirmed} onChange={(e) => setApproveConfirmed(e.target.checked)} className="mt-0.5" />
              I confirm I have reviewed the fabric specifications and images for this option.
            </label>
          </div>
        )}
      </Modal>

      <Modal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        title={rejectMode === DECISION.MORE_OPTIONS ? 'Request More Options' : 'Reject Fabric Option'}
        subtitle={rejectTarget?.name}
        size="sm"
        footer={
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button variant="danger" icon={<XCircle size={14} />} disabled={!rejectReason} onClick={confirmReject}>Confirm</Button>
          </div>
        }
      >
        <div className="space-y-4">
          {rejectMode === DECISION.REJECTED && (
            <div>
              <label className="text-xs font-semibold text-[#5C5A54]">Rejection reason *</label>
              <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                {REJECT_REASONS.map((r) => (
                  <label key={r} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors text-xs ${rejectReason === r ? 'bg-[#FDECEA] border-[#F5A9AC] text-[#C0222B] font-medium' : 'border-[#E5E3DE] text-[#5C5A54] hover:bg-[#F4F3F0]'}`}>
                    <input type="radio" name="reject-reason" value={r} checked={rejectReason === r} onChange={() => setRejectReason(r)} className="sr-only" />
                    {r}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-[#5C5A54]">Comments for the manufacturer</label>
            <textarea rows={2} value={rejectComment} onChange={(e) => setRejectComment(e.target.value)} className="w-full mt-1.5 px-3 py-2 text-sm border border-[#E5E3DE] rounded-lg outline-none focus:border-[#3D52A0] resize-none" />
          </div>
        </div>
      </Modal>
    </div>
  );
}
